import React, { useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Zap, CheckCircle2, TrendingUp, Check, Copy, Link2,
  ArrowRight, User, Shield, Lock, Truck, Briefcase,
} from "lucide-react";
import { toast } from "sonner";

// ── constants (mirrored from BidModal) ──────────────────────────────────────
const BID_ACCENT = "#f59e0b";
const GOLD       = "#F5C842";
const DARK       = "#030305";
const CARD       = "#0e0e12";
const MID        = "#111116";

const EQUIPMENT_PACKAGES = {
  semi_truck: ["Dry Van", "Reefer", "Flatbed", "Step Deck", "Tanker", "Lowboy", "Power Only"],
  box_truck:  ["26ft Box", "20ft Box", "Sprinter/Cargo Van", "Hotshot"],
};
const PAY_PROGRAMS_UI = {
  company_driver: { label: "Company Driver", payType: "cpm",        min: 0.70, placeholder: "0.75", unit: "¢/mi CPM" },
  rental:         { label: "Rental",         payType: "percentage", min: 85,   placeholder: "85",   unit: "% per load" },
  lease:          { label: "Lease Purchase", payType: "percentage", min: 85,   placeholder: "85",   unit: "% per load" },
  owner_operator: { label: "Owner Operator", payType: "percentage", min: 88,   placeholder: "88",   unit: "% gross" },
};
const EQUIPMENT_CPM_MIN_UI = {
  "Dry Van":0.70,"Reefer":0.80,"Flatbed":0.95,"Step Deck":0.90,
  "Tanker":0.85,"Lowboy":0.90,"Power Only":0.70,
  "26ft Box":0.55,"20ft Box":0.55,"Sprinter/Cargo Van":0.55,"Hotshot":0.55,
};
const PROGRAM_MIN_FEE_UI = {
  semi_truck: { company_driver:1500, rental:1700, lease:1900, owner_operator:2100 },
  box_truck:  { company_driver: 800, rental:1000, lease:1200, owner_operator:1400 },
};
const PROGRAM_DISCOUNT_UI = {
  semi_truck: { company_driver:300, rental:400, lease:500, owner_operator:600 },
  box_truck:  { company_driver:200, rental:250, lease:300, owner_operator:350 },
};
const WEEKLY_FLEX_PREVIEW = {
  semi_truck: {
    company_driver: [{weeks:9,price:275},{weeks:17,price:185,popular:true},{weeks:35,price:120},{weeks:52,price:95,daily:"~$14/day"}],
    rental:         [{weeks:9,price:310},{weeks:17,price:205,popular:true},{weeks:35,price:135},{weeks:52,price:110,daily:"~$16/day"}],
    lease:          [{weeks:9,price:340},{weeks:17,price:230,popular:true},{weeks:35,price:150},{weeks:52,price:125,daily:"~$18/day"}],
    owner_operator: [{weeks:9,price:375},{weeks:17,price:255,popular:true},{weeks:35,price:165},{weeks:52,price:135,daily:"~$19/day"}],
  },
  box_truck: {
    company_driver: [{weeks:9,price:150},{weeks:17,price:100,popular:true},{weeks:35,price:65},{weeks:52,price:55,daily:"~$8/day"}],
    rental:         [{weeks:9,price:180},{weeks:17,price:125,popular:true},{weeks:35,price:80},{weeks:52,price:65,daily:"~$9/day"}],
    lease:          [{weeks:9,price:215},{weeks:17,price:150,popular:true},{weeks:35,price:95},{weeks:52,price:80,daily:"~$11/day"}],
    owner_operator: [{weeks:9,price:250},{weeks:17,price:170,popular:true},{weeks:35,price:110},{weeks:52,price:90,daily:"~$13/day"}],
  },
};
const PAYMENT_OPTIONS = [
  { key:"standard",         label:"Standard 50/50",    desc:"50% on hire day · 50% after 2 weeks" },
  { key:"one_time_discount",label:"One-Time Payment",  desc:"Pay once upfront · save on placement fee" },
  { key:"weekly_flex",      label:"Hylas Weekly Flex", desc:"Fixed weekly payments · flexible duration" },
];
const DEMO_DRIVER = {
  first_name: "Alex", last_name: "Driver",
  cdl_class: "CDL-A", state: "TX", position: "OTR Driver",
  years_exp: 7, bid_count: 4,
};

const inputCls = "w-full bg-[#111] border border-white/10 text-white px-3 py-2.5 text-sm focus:outline-none focus:border-[#f59e0b] transition-colors";
const labelCls = "block text-xs font-head uppercase tracking-[0.2em] text-white/50 mb-1.5";

// ── Registration Gate Modal ──────────────────────────────────────────────────
function RegGate({ onClose }) {
  const nav = useNavigate();
  return (
    <motion.div
      initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background:"rgba(0,0,0,0.92)", backdropFilter:"blur(12px)" }}
      onClick={(e)=>{ if(e.target===e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ opacity:0, scale:0.96, y:12 }}
        animate={{ opacity:1, scale:1, y:0 }}
        exit={{ opacity:0, scale:0.96 }}
        transition={{ duration:0.22 }}
        className="relative w-full max-w-md"
        style={{ background:CARD, border:`1px solid rgba(245,158,11,0.32)`, boxShadow:`0 0 80px rgba(245,158,11,0.14)` }}
      >
        <div style={{ height:3, background:`linear-gradient(90deg,${BID_ACCENT},${GOLD})` }} />

        <button onClick={onClose} className="absolute top-4 right-4 text-white/30 hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>

        <div className="p-7 text-center">
          <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center"
            style={{ background:"rgba(245,158,11,0.1)", border:`1px solid rgba(245,158,11,0.3)` }}>
            <Lock className="w-5 h-5" style={{ color:BID_ACCENT }} />
          </div>
          <p className="font-head text-[10px] uppercase tracking-[0.3em] text-white/30 mb-2">Demo Complete</p>
          <h2 className="font-head font-black text-2xl uppercase tracking-[0.04em] text-white mb-2">
            Ready to place<br/>a real bid?
          </h2>
          <p className="text-sm text-white/40 leading-relaxed mb-7">
            You've seen how pricing works. Create your carrier account — free to join, bids go live instantly.
          </p>

          <div className="space-y-3">
            <button
              data-testid="pricing-demo-register-btn"
              onClick={() => nav("/market")}
              className="w-full flex items-center justify-center gap-2.5 py-3.5 font-head text-sm font-black uppercase tracking-[0.2em] transition-all"
              style={{ background:`linear-gradient(90deg,${BID_ACCENT},#d97706)`, color:"white", boxShadow:"0 4px 24px rgba(245,158,11,0.3)" }}
              onMouseEnter={(e)=>{ e.currentTarget.style.boxShadow="0 4px 40px rgba(245,158,11,0.5)"; }}
              onMouseLeave={(e)=>{ e.currentTarget.style.boxShadow="0 4px 24px rgba(245,158,11,0.3)"; }}
            >
              <User className="w-3.5 h-3.5" />
              Create Carrier Account — Free
            </button>
            <button
              data-testid="pricing-demo-login-btn"
              onClick={() => nav("/market")}
              className="w-full flex items-center justify-center gap-2.5 py-3 font-head text-sm font-bold uppercase tracking-[0.18em] border transition-all"
              style={{ borderColor:"rgba(255,255,255,0.12)", color:"rgba(255,255,255,0.65)", background:"transparent" }}
              onMouseEnter={(e)=>{ e.currentTarget.style.borderColor="rgba(255,255,255,0.3)"; e.currentTarget.style.color="#fff"; }}
              onMouseLeave={(e)=>{ e.currentTarget.style.borderColor="rgba(255,255,255,0.12)"; e.currentTarget.style.color="rgba(255,255,255,0.65)"; }}
            >
              Already a Carrier? Login
            </button>
          </div>

          <p className="text-[10px] text-white/20 mt-5 font-head uppercase tracking-[0.12em]">
            No commitment · Cancel anytime
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Main Pricing Demo Page ───────────────────────────────────────────────────
export default function MarketPricingDemo() {
  const [form, setForm] = useState({
    position_type:         "semi_truck",
    equipment_package:     "Dry Van",
    pay_program:           "company_driver",
    driver_pay_cpm:        "",
    driver_pay_percentage: "",
    driver_pay_package:    "",
    placement_fee:         "",
    payment_option:        "standard",
    offer_truck_model:     "",
    offer_truck_year_from: "",
    offer_truck_year_to:   "",
    offer_weeks_out:       "",
    offer_days_off:        "",
    offer_miles_from:      "",
    offer_miles_to:        "",
    offer_signup_bonus:    "",
    offer_safety_bonus:    "",
    offer_dotw_bonus:      "",
    offer_dotm_bonus:      "",
  });
  const [flexDuration, setFlexDuration] = useState(17);
  const [showGate, setShowGate]         = useState(false);
  const [error, setError]               = useState("");
  const [copied, setCopied]             = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const isCpmProgram    = form.pay_program === "company_driver";
  const equipCpmMin     = EQUIPMENT_CPM_MIN_UI[form.equipment_package] ?? 0.70;
  const minPay          = isCpmProgram ? equipCpmMin : (PAY_PROGRAMS_UI[form.pay_program]?.min ?? 85);
  const baseFee         = PROGRAM_MIN_FEE_UI[form.position_type]?.[form.pay_program] ?? 1500;
  const oneTimeDiscount = PROGRAM_DISCOUNT_UI[form.position_type]?.[form.pay_program] ?? 300;
  const isOneTime       = form.payment_option === "one_time_discount";
  const effectiveMinFee = isOneTime ? Math.max(0, baseFee - oneTimeDiscount) : baseFee;
  const flexPreview     = WEEKLY_FLEX_PREVIEW[form.position_type]?.[form.pay_program] || WEEKLY_FLEX_PREVIEW.semi_truck.company_driver;

  function handleCopyLink() {
    const url = `https://hylascompanion.com/market/pricing`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      toast.success("Link copied — share it with carriers!");
      setTimeout(() => setCopied(false), 2500);
    }).catch(() => {
      toast.error("Could not copy — please copy the URL from your address bar.");
    });
  }

  function handleSubmit(e) {
    e.preventDefault();
    setError("");
    const isWeeklyFlex = form.payment_option === "weekly_flex";
    if (!isWeeklyFlex) {
      const fee = parseInt(form.placement_fee, 10);
      if (isNaN(fee) || fee < effectiveMinFee) {
        setError(`Floor for ${PAY_PROGRAMS_UI[form.pay_program]?.label}: $${effectiveMinFee.toLocaleString()} — adjust and try again.`);
        return;
      }
    }
    if (isCpmProgram && (!form.driver_pay_cpm || parseFloat(form.driver_pay_cpm) < equipCpmMin)) {
      setError(`Min CPM for ${form.equipment_package}: $${equipCpmMin.toFixed(2)}/mi`); return;
    }
    if (!isCpmProgram && (!form.driver_pay_percentage || parseFloat(form.driver_pay_percentage) < minPay)) {
      setError(`Min rate for ${PAY_PROGRAMS_UI[form.pay_program]?.label}: ${minPay}%`); return;
    }
    setShowGate(true);
  }

  return (
    <>
      <Helmet>
        <title>Carrier Pricing Demo — Hylas Companion Market</title>
        <meta name="description" content="See how driver placement pricing works — try the live bidding simulator before registering." />
      </Helmet>

      <div className="min-h-screen pt-20" style={{ background: DARK, color:"#e8e8e8" }}>
        {/* ── Top bar ─────────────────────────────────────────────────────── */}
        <div style={{ background: MID, borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
          <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-0.5 h-5" style={{ background: GOLD }} />
              <span className="font-head text-xs font-black uppercase tracking-[0.35em] text-white">Hylas Companion</span>
              <span className="font-head text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 font-black"
                style={{ background:"rgba(245,158,11,0.12)", color:BID_ACCENT, border:`1px solid rgba(245,158,11,0.3)` }}>
                Live Pricing Demo
              </span>
            </div>
            <button
              data-testid="copy-pricing-link"
              onClick={handleCopyLink}
              className="flex items-center gap-1.5 px-3 py-2 font-head text-[10px] uppercase tracking-[0.2em] border transition-all"
              style={{ borderColor: copied ? "rgba(34,197,94,0.4)" : "rgba(255,255,255,0.1)", color: copied ? "#22c55e" : "rgba(255,255,255,0.5)", background:"transparent" }}
            >
              {copied ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? "Copied!" : "Copy Share Link"}
            </button>
          </div>
        </div>

        {/* ── Hero ────────────────────────────────────────────────────────── */}
        <div className="max-w-5xl mx-auto px-4 pt-10 pb-8">
          <div className="flex flex-col lg:flex-row gap-10 items-start">

            {/* Left — context */}
            <div className="lg:w-72 shrink-0">
              <p className="font-head text-[10px] uppercase tracking-[0.35em] mb-3" style={{ color:"rgba(245,158,11,0.6)" }}>
                How It Works
              </p>
              <h1 className="font-head font-black text-3xl uppercase leading-[1.1] mb-4" style={{ letterSpacing:"-0.01em" }}>
                Try the<br/><span style={{ color:BID_ACCENT }}>Bid System</span><br/>Live.
              </h1>
              <p className="text-sm text-white/40 leading-relaxed mb-6">
                This is the exact same pricing form real carriers use. Configure the vehicle type, pay program, and payment plan — see your exact floor price update in real time.
              </p>

              {/* Plan bullets */}
              <div className="space-y-3 mb-8">
                {[
                  { icon: Briefcase, label:"Standard 50/50", detail:"Split across 2 milestones" },
                  { icon: Zap,       label:"One-Time Discount", detail:"Save upfront, pay once" },
                  { icon: TrendingUp,label:"Weekly Flex", detail:"From $55/week — auto-stops" },
                ].map(({ icon:Icon, label, detail }) => (
                  <div key={label} className="flex items-center gap-3 px-3 py-2.5"
                    style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)" }}>
                    <Icon className="w-3.5 h-3.5 shrink-0" style={{ color:BID_ACCENT }} />
                    <div>
                      <p className="font-head text-xs font-bold uppercase tracking-[0.08em] text-white">{label}</p>
                      <p className="text-[10px] text-white/35 mt-0.5">{detail}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="px-4 py-4" style={{ background:"rgba(245,158,11,0.04)", border:"1px solid rgba(245,158,11,0.15)" }}>
                <p className="font-head text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color:GOLD }}>Share This Page</p>
                <div className="flex items-center gap-2 mb-3"
                  style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)", padding:"8px 10px" }}>
                  <Link2 className="w-3 h-3 shrink-0 text-white/25" />
                  <span className="text-[10px] text-white/35 font-mono truncate">hylascompanion.com/market/pricing</span>
                </div>
                <button
                  onClick={handleCopyLink}
                  className="w-full flex items-center justify-center gap-2 py-2.5 font-head text-[10px] uppercase tracking-[0.2em] border transition-all"
                  style={{ borderColor: copied ? "rgba(34,197,94,0.4)" : "rgba(245,158,11,0.3)", color: copied ? "#22c55e" : GOLD, background:"transparent" }}
                >
                  {copied ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copied ? "Copied!" : "Copy & Share"}
                </button>
              </div>
            </div>

            {/* Right — bid form */}
            <div className="flex-1 w-full">
              {/* Demo driver card */}
              <div className="flex items-center gap-4 px-5 py-4 mb-4"
                style={{ background:MID, border:`1px solid rgba(245,158,11,0.2)` }}>
                <div className="w-10 h-10 flex items-center justify-center font-head font-black text-base"
                  style={{ background:"rgba(245,158,11,0.12)", color:BID_ACCENT, border:`1px solid rgba(245,158,11,0.3)` }}>
                  {DEMO_DRIVER.first_name[0]}{DEMO_DRIVER.last_name[0]}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-head font-black text-base uppercase tracking-[0.05em] text-white">
                      {DEMO_DRIVER.first_name} {DEMO_DRIVER.last_name}
                    </p>
                    <span className="font-head text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 font-black"
                      style={{ background:"rgba(245,158,11,0.1)", color:BID_ACCENT, border:`1px solid rgba(245,158,11,0.25)` }}>
                      Demo Driver
                    </span>
                  </div>
                  <p className="font-head text-xs uppercase tracking-[0.12em] text-white/40 mt-0.5">
                    {DEMO_DRIVER.cdl_class} · {DEMO_DRIVER.state} · {DEMO_DRIVER.years_exp} yrs exp · {DEMO_DRIVER.bid_count} active bids
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-head text-[10px] uppercase tracking-[0.15em] text-white/30">Floor</p>
                  <p className="font-head font-black text-xl" style={{ color:BID_ACCENT }}>
                    ${effectiveMinFee.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Bid Form */}
              <div style={{ background:CARD, border:`1px solid rgba(245,158,11,0.18)`, boxShadow:`0 0 40px rgba(245,158,11,0.06)` }}>
                <div style={{ height:3, background:`linear-gradient(90deg,${BID_ACCENT},${GOLD})` }} />

                <div className="px-5 pb-3 pt-4 flex items-center gap-2">
                  <Truck className="w-4 h-4" style={{ color:BID_ACCENT }} />
                  <p className="font-head text-[10px] uppercase tracking-[0.3em] text-white/40">Live Bid Simulator</p>
                  <span className="ml-auto font-head text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 animate-pulse"
                    style={{ background:"rgba(245,158,11,0.08)", color:"rgba(245,158,11,0.6)", border:"1px solid rgba(245,158,11,0.2)" }}>
                    Demo Mode
                  </span>
                </div>

                <form onSubmit={handleSubmit} className="px-5 pb-6 space-y-5">
                  {/* Vehicle type */}
                  <div>
                    <label className={labelCls}>Vehicle Type</label>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(EQUIPMENT_PACKAGES).map(([key]) => (
                        <button key={key} type="button"
                          data-testid={`demo-vehicle-${key}`}
                          onClick={() => setForm((f) => ({ ...f, position_type:key, equipment_package:EQUIPMENT_PACKAGES[key][0], driver_pay_cpm:"", driver_pay_percentage:"" }))}
                          className="py-3 font-head text-sm uppercase tracking-[0.08em] transition-all border"
                          style={{
                            background:  form.position_type===key ? "rgba(245,158,11,0.1)" : "rgba(255,255,255,0.02)",
                            color:       form.position_type===key ? BID_ACCENT : "#9CA3AF",
                            borderColor: form.position_type===key ? BID_ACCENT : "#2a2a2a",
                          }}>
                          {key==="semi_truck" ? "Semi Truck" : "Box Truck"}
                          <span className="block text-xs mt-0.5" style={{ color:form.position_type===key ? "rgba(245,158,11,0.65)" : "#6B7280" }}>
                            {key==="semi_truck" ? "$1,500–$2,100+ floor" : "$800–$1,400+ floor"}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Equipment */}
                  <div>
                    <label className={labelCls}>Equipment Package</label>
                    <select value={form.equipment_package}
                      onChange={(e) => setForm((f) => ({ ...f, equipment_package:e.target.value, driver_pay_cpm:"" }))}
                      data-testid="demo-equipment-select"
                      className={inputCls} style={{ background:"#111" }}>
                      {(EQUIPMENT_PACKAGES[form.position_type]||[]).map((e) => (
                        <option key={e} value={e}>{e}</option>
                      ))}
                    </select>
                  </div>

                  {/* Pay program */}
                  <div>
                    <label className={labelCls}>Pay Program</label>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(PAY_PROGRAMS_UI).map(([key, prog]) => (
                        <button key={key} type="button"
                          data-testid={`demo-pay-${key}`}
                          onClick={() => setForm((f) => ({ ...f, pay_program:key, driver_pay_cpm:"", driver_pay_percentage:"" }))}
                          className="py-2.5 font-head text-sm uppercase tracking-[0.08em] transition-all border"
                          style={{
                            background:  form.pay_program===key ? "rgba(245,158,11,0.1)" : "rgba(255,255,255,0.02)",
                            color:       form.pay_program===key ? BID_ACCENT : "#9CA3AF",
                            borderColor: form.pay_program===key ? BID_ACCENT : "#2a2a2a",
                          }}>
                          {prog.label}
                          <span className="block text-xs mt-0.5" style={{ color:form.pay_program===key ? "rgba(245,158,11,0.6)" : "#6B7280" }}>
                            Min ${(PROGRAM_MIN_FEE_UI[form.position_type]?.[key]||800).toLocaleString()} · {key==="company_driver" ? (form.position_type==="semi_truck"?"$0.70 CPM":"$0.55 CPM") : `${prog.min}%`}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Driver pay */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      {isCpmProgram ? (
                        <>
                          <label className={labelCls}>Driver Pay (CPM $)</label>
                          <input type="number" step="0.01" min={equipCpmMin} value={form.driver_pay_cpm}
                            onChange={set("driver_pay_cpm")} placeholder={equipCpmMin.toFixed(2)}
                            data-testid="demo-driver-pay-cpm" className={inputCls} />
                          <p className="text-white/40 text-xs mt-1.5">Min ${equipCpmMin.toFixed(2)}/mi · {form.equipment_package}</p>
                        </>
                      ) : (
                        <>
                          <label className={labelCls}>Driver % {PAY_PROGRAMS_UI[form.pay_program]?.unit}</label>
                          <input type="number" step="0.5" min={minPay} max="100" value={form.driver_pay_percentage}
                            onChange={set("driver_pay_percentage")} placeholder={`${minPay}`}
                            data-testid="demo-driver-pay-pct" className={inputCls} />
                          <p className="text-white/40 text-xs mt-1.5">Min {minPay}%</p>
                        </>
                      )}
                    </div>
                    <div>
                      <label className={labelCls}>Pay Note (optional)</label>
                      <input type="text" value={form.driver_pay_package} onChange={set("driver_pay_package")}
                        placeholder={isCpmProgram ? "e.g. 85¢ + Reefer" : "e.g. 88% OTR"} className={inputCls} />
                    </div>
                  </div>

                  {/* Placement fee */}
                  <div>
                    <label className={labelCls}>Placement Fee to Hylas ($)</label>
                    <input type="number" value={form.placement_fee} onChange={set("placement_fee")}
                      placeholder={`${effectiveMinFee}`} data-testid="demo-placement-fee" className={inputCls} />
                    <p className="text-white/40 text-xs mt-1.5">
                      Floor:{" "}
                      <span style={{ color:BID_ACCENT }}>${effectiveMinFee.toLocaleString()}</span>
                      {isOneTime && <span style={{ color:GOLD }}> (−${oneTimeDiscount.toLocaleString()} one-time discount)</span>}
                      {" "}· No maximum cap — bid higher to win
                    </p>

                    {DEMO_DRIVER.bid_count > 0 && (
                      <div className="mt-3 px-4 py-3" style={{ background:"rgba(245,200,66,0.05)", border:"1px solid rgba(245,200,66,0.22)" }}>
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-3.5 h-3.5 shrink-0" style={{ color:GOLD }} />
                          <p className="font-head text-xs font-black uppercase tracking-[0.08em]" style={{ color:GOLD }}>
                            {DEMO_DRIVER.bid_count} carriers are competing for this driver right now.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Payment structure */}
                  <div>
                    <label className={labelCls}>Payment Structure</label>
                    <div className="space-y-2">
                      {PAYMENT_OPTIONS.map((opt) => (
                        <button key={opt.key} type="button"
                          data-testid={`demo-payment-${opt.key}`}
                          onClick={() => setForm((f) => ({ ...f, payment_option:opt.key }))}
                          className="w-full flex items-start gap-3 px-4 py-3.5 text-left transition-all border"
                          style={{
                            background:  form.payment_option===opt.key ? "rgba(245,158,11,0.08)" : "rgba(255,255,255,0.02)",
                            borderColor: form.payment_option===opt.key ? BID_ACCENT : "#222",
                            boxShadow:   form.payment_option===opt.key ? `0 0 12px rgba(245,158,11,0.1)` : "none",
                          }}>
                          <div className="mt-0.5 w-3.5 h-3.5 rounded-full border flex-shrink-0 flex items-center justify-center"
                            style={{ borderColor:form.payment_option===opt.key ? BID_ACCENT : "#555", background:form.payment_option===opt.key ? BID_ACCENT : "transparent" }} />
                          <div>
                            <p className="font-head text-sm font-bold uppercase tracking-[0.1em]"
                              style={{ color:form.payment_option===opt.key ? BID_ACCENT : "#D1D5DB" }}>
                              {opt.label}
                              {opt.key==="weekly_flex" && (
                                <span className="ml-2 text-[9px] font-black px-1.5 py-0.5 align-middle"
                                  style={{ background:"rgba(245,200,66,0.15)", color:GOLD, border:"1px solid rgba(245,200,66,0.3)" }}>Interest</span>
                              )}
                            </p>
                            {opt.key==="weekly_flex" ? (
                              <p className="text-xs mt-0.5" style={{ color:"#6B7280" }}>
                                From{" "}<span className="font-head font-black" style={{ color:GOLD }}>$55/week</span>{" "}· billing stops on driver exit
                              </p>
                            ) : opt.key==="one_time_discount" ? (
                              <p className="text-xs mt-0.5" style={{ color:"#6B7280" }}>
                                Pay once upfront ·{" "}
                                <span className="font-head font-black" style={{ color:GOLD }}>
                                  save ${(PROGRAM_DISCOUNT_UI[form.position_type]?.[form.pay_program]??300).toLocaleString()}
                                </span>
                              </p>
                            ) : (
                              <p className="text-xs mt-0.5" style={{ color:"#6B7280" }}>{opt.desc}</p>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Weekly Flex selector */}
                  {form.payment_option==="weekly_flex" && (
                    <motion.div initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }} className="space-y-3.5"
                      style={{ background:"rgba(245,200,66,0.03)", border:"1px solid rgba(245,200,66,0.18)", padding:"16px" }}>
                      <span className="font-head text-sm font-black uppercase tracking-[0.15em]" style={{ color:GOLD }}>Select Your Plan</span>
                      <div className="grid grid-cols-4 gap-1.5">
                        {flexPreview.map((opt) => {
                          const isSel = flexDuration===opt.weeks;
                          return (
                            <button key={opt.weeks} type="button"
                              data-testid={`demo-flex-${opt.weeks}`}
                              onClick={() => setFlexDuration(opt.weeks)}
                              className="relative p-2.5 border text-center transition-all"
                              style={{
                                background: isSel ? "rgba(245,200,66,0.12)" : opt.popular ? "rgba(245,200,66,0.04)" : "rgba(255,255,255,0.02)",
                                borderColor: isSel ? GOLD : opt.popular ? "rgba(245,200,66,0.3)" : "#2a2a2a",
                                outline: isSel ? `2px solid ${GOLD}` : "none", outlineOffset:"1px",
                              }}>
                              {opt.popular && !isSel && (
                                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 font-black text-[7px] uppercase px-1 py-0.5 whitespace-nowrap"
                                  style={{ background:GOLD, color:"#000" }}>Popular</span>
                              )}
                              <p className="font-head text-xs font-bold" style={{ color: isSel ? "#fff" : opt.popular ? "#fff" : "#9CA3AF" }}>{opt.weeks} Wks</p>
                              <p className="text-sm font-black mt-0.5" style={{ color:GOLD }}>${opt.price}/wk</p>
                              {opt.daily && <p className="text-[9px] mt-0.5" style={{ color:"#6B7280" }}>{opt.daily}</p>}
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}

                  {/* Offer details — optional */}
                  <div className="space-y-3">
                    <p className={labelCls} style={{ color:"rgba(255,255,255,0.3)" }}>
                      Personal Offer Details <span className="normal-case font-normal text-white/20">(optional — driver sees this anonymously)</span>
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/25 mb-1">Weeks Out</p>
                        <select value={form.offer_weeks_out} onChange={set("offer_weeks_out")} className={inputCls+" appearance-none"} style={{ background:"#111" }}>
                          <option value="">— Not specified</option>
                          {["1","2","3","4"].map(w=><option key={w} value={w}>{w} week{w!=="1"?"s":""} out</option>)}
                        </select>
                      </div>
                      <div>
                        <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/25 mb-1">Days Off</p>
                        <select value={form.offer_days_off} onChange={set("offer_days_off")} className={inputCls+" appearance-none"} style={{ background:"#111" }}>
                          <option value="">— Not specified</option>
                          {["2","3","4","5","6","7"].map(d=><option key={d} value={d}>{d} days off</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/25 mb-2">Bonuses Offered</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          {key:"offer_signup_bonus",label:"Signup Bonus"},
                          {key:"offer_safety_bonus",label:"Safety Bonus"},
                          {key:"offer_dotw_bonus",  label:"Driver of the Week"},
                          {key:"offer_dotm_bonus",  label:"Driver of the Month"},
                        ].map(({key,label})=>(
                          <div key={key}>
                            <p className="font-head text-[8px] uppercase tracking-[0.15em] text-white/20 mb-1">{label}</p>
                            <select value={form[key]} onChange={set(key)} className={inputCls+" appearance-none"} style={{ background:"#111" }}>
                              <option value="">— None</option>
                              {Array.from({length:30},(_,i)=>(i+1)*100).map(v=>(
                                <option key={v} value={v}>${v.toLocaleString()}</option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Error */}
                  <AnimatePresence>
                    {error && (
                      <motion.p initial={{ opacity:0, y:-6 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}
                        className="text-red-400 text-sm font-head bg-red-400/10 px-3 py-2">
                        {error}
                      </motion.p>
                    )}
                  </AnimatePresence>

                  {/* Submit */}
                  <motion.button
                    type="submit"
                    data-testid="demo-submit-bid"
                    whileTap={{ scale:0.97 }}
                    className="w-full flex items-center justify-center gap-2 py-3.5 font-head text-sm font-black uppercase tracking-[0.2em] transition-all"
                    style={{
                      background: `linear-gradient(90deg,${BID_ACCENT},#d97706)`,
                      color:"white",
                      boxShadow:"0 4px 20px rgba(245,158,11,0.25)",
                    }}
                    onMouseEnter={(e)=>{ e.currentTarget.style.boxShadow="0 4px 32px rgba(245,158,11,0.45)"; }}
                    onMouseLeave={(e)=>{ e.currentTarget.style.boxShadow="0 4px 20px rgba(245,158,11,0.25)"; }}
                  >
                    <Zap className="w-3.5 h-3.5" />
                    {form.payment_option==="weekly_flex" ? "Submit Flex Bid — Goes Live Instantly" : "Submit Bid — Goes Live Instantly"}
                  </motion.button>
                  <p className="text-white/20 text-xs text-center font-head">
                    Demo mode — create your account to place a real bid
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* ── Bottom CTA ──────────────────────────────────────────────────── */}
        <div style={{ background:MID, borderTop:"1px solid rgba(255,255,255,0.05)" }}>
          <div className="max-w-5xl mx-auto px-4 py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <p className="font-head text-xs uppercase tracking-[0.25em] mb-1" style={{ color:"rgba(245,158,11,0.5)" }}>Ready to hire?</p>
              <p className="font-head font-black text-xl text-white uppercase">Create your carrier account — free.</p>
              <p className="text-sm text-white/35 mt-1">Bids go live instantly. Hylas admin picks the winner via the Transfer Board.</p>
            </div>
            <a href="/market"
              className="shrink-0 inline-flex items-center gap-2 px-7 py-3.5 font-head text-sm font-black uppercase tracking-[0.2em] transition-all"
              style={{ background:`linear-gradient(90deg,${BID_ACCENT},#d97706)`, color:"white", boxShadow:"0 4px 20px rgba(245,158,11,0.25)" }}
              onMouseEnter={(e)=>{ e.currentTarget.style.boxShadow="0 4px 40px rgba(245,158,11,0.5)"; }}
              onMouseLeave={(e)=>{ e.currentTarget.style.boxShadow="0 4px 20px rgba(245,158,11,0.25)"; }}>
              Register & Start Bidding <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Registration Gate */}
      <AnimatePresence>
        {showGate && <RegGate onClose={() => setShowGate(false)} />}
      </AnimatePresence>
    </>
  );
}
